import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api';
import { extractErrorMessage } from '../utils/errorHandling';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import CollapsibleFilters from './CollapsibleFilters';

interface NDRRecord {
  id: number;
  ndr_number: string;
  order_id: number;
  order_number: string | null;
  order_status: string | null;
  order_shipping_status: string | null;
  awb_number: string | null;
  shipping_provider: string | null;
  status: string;
  attempt_count: number;
  reason: string | null;
  reason_code: string | null;
  action: string | null;
  reschedule_date: string | null;
  updated_address: string | null;
  updated_phone: string | null;
  resolution_notes: string | null;
  warehouse: string | null;
  resolved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface NDRSummary {
  open: number;
  delivered: number;
  rto_initiated: number;
  closed: number;
  total: number;
  by_status: Record<string, number>;
  period?: string;
}

interface NDRManagementProps {
  userRole?: string;
}

const STATUS_BADGE: Record<string, { color: string; bg: string; label: string }> = {
  open: { color: '#856404', bg: '#fff3cd', label: 'Open' },
  reattempt_requested: { color: '#004085', bg: '#cce5ff', label: 'Reattempt Requested' },
  rescheduled: { color: '#0c5460', bg: '#d1ecf1', label: 'Rescheduled' },
  address_updated: { color: '#6b21a8', bg: '#f3e8ff', label: 'Address Updated' },
  delivered: { color: '#155724', bg: '#d4edda', label: 'Delivered' },
  rto_initiated: { color: '#721c24', bg: '#f8d7da', label: 'RTO Initiated' },
  closed: { color: '#383d41', bg: '#d6d8db', label: 'Closed' },
};

const PROVIDER_OPTIONS = ['Delhivery', 'BlueDart'];
const STATUS_OPTIONS = Object.keys(STATUS_BADGE);
const OPEN_STATUSES = ['open', 'reattempt_requested', 'rescheduled', 'address_updated'];

type ActionType = 'reattempt' | 'reschedule' | 'update_address' | 'mark_rto' | 'close';

// Plain-language description of what each action will actually do, shown in
// the confirmation modal so a mis-click on a row button can be caught before
// a key workflow fires.
const ACTION_EFFECT: Record<ActionType, string> = {
  reattempt: 'This will ask the carrier to attempt delivery again.',
  reschedule: 'This will request the carrier to deliver on the chosen date.',
  update_address: 'This will send a corrected delivery address for the next attempt.',
  mark_rto: 'This will initiate a Return-to-Origin: the order is marked returned and an RTO return record is created for restocking. This cannot be undone from here.',
  close: 'This will close and resolve the NDR. It will no longer appear under open items.',
};

// Order shipping_status values for which an action is NOT applicable. Mirrors
// the backend guard in ndr_routes._assert_action_applicable so the UI can warn
// (and disable confirm) before the request is sent. 'close' is always allowed.
const ACTION_BLOCKED_STATES: Record<ActionType, string[]> = {
  reattempt: ['delivered', 'returned', 'in_transit', 'out_for_delivery', 'booked', 'picked_up'],
  reschedule: ['delivered', 'returned', 'in_transit', 'out_for_delivery', 'booked', 'picked_up'],
  update_address: ['delivered', 'returned', 'in_transit', 'out_for_delivery', 'booked', 'picked_up'],
  mark_rto: ['delivered', 'returned'],
  close: [],
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_BADGE[status] || { color: '#383d41', bg: '#d6d8db', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px', borderRadius: '12px',
      fontSize: '11px', fontWeight: 600, color: s.color, background: s.bg, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
};

/** Typeahead select for a fixed option list (client-side filter). */
const Typeahead: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  labelMap?: (o: string) => string;
}> = ({ value, onChange, options, placeholder, labelMap }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const display = (o: string) => (labelMap ? labelMap(o) : o);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => display(o).toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        value={open ? query : (value ? display(value) : '')}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        style={{
          padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px',
          fontSize: '13px', background: 'white', width: '100%', boxSizing: 'border-box',
        }}
      />
      {value && !open && (
        <button
          onClick={() => onChange('')}
          title="Clear"
          style={{
            position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '14px',
          }}
        >×</button>
      )}
      {open && (
        <div style={{
          position: 'absolute', background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflow: 'auto',
          zIndex: 100, width: '100%', marginTop: '2px',
        }}>
          <div
            onClick={() => { onChange(''); setOpen(false); }}
            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9', color: '#94a3b8' }}
          >All</div>
          {filtered.map(o => (
            <div
              key={o}
              onClick={() => { onChange(o); setOpen(false); }}
              onMouseDown={(e) => e.preventDefault()}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
            >{display(o)}</div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8' }}>No matches</div>
          )}
        </div>
      )}
    </div>
  );
};

const NDRManagement: React.FC<NDRManagementProps> = ({ userRole = 'viewer' }) => {
  const canAct = ['admin', 'manager', 'staff'].includes(userRole);
  const canRTO = ['admin', 'manager'].includes(userRole);

  const [records, setRecords] = useState<NDRRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<NDRSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [openOnly, setOpenOnly] = useState(true);
  // Window by created_at: 'month' (current calendar month, default), '90d',
  // '180d', or 'all'. Matches the backend `period` param.
  const [period, setPeriod] = useState('month');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Sorting (client-side on current page)
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Action modal
  const [actionModal, setActionModal] = useState<{ ndr: NDRRecord; action: ActionType } | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [rtoAcknowledged, setRtoAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        period,
      };
      if (openOnly) params.open_only = true;
      if (filterStatus) params.status = filterStatus;
      if (filterProvider) params.provider = filterProvider;
      if (search.trim()) params.q = search.trim();

      const [listRes, sumRes] = await Promise.all([
        api.get('/api/ndr/', { params }),
        api.get('/api/ndr/summary', { params: { period } }),
      ]);
      const data = listRes.data;
      setRecords(Array.isArray(data.items) ? data.items : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setSummary(sumRes.data || null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, openOnly, filterStatus, filterProvider, search, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedRecords = useMemo(() => {
    const arr = [...records];
    arr.sort((a, b) => {
      const av = (a as any)[sortField];
      const bv = (b as any)[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortOrder === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [records, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  const openAction = (ndr: NDRRecord, action: ActionType) => {
    setActionModal({ ndr, action });
    setActionNotes('');
    setRescheduleDate('');
    setNewAddress(ndr.updated_address || '');
    setNewPhone(ndr.updated_phone || '');
    setRtoAcknowledged(false);
  };

  // Is the requested action applicable to the order's current shipping state?
  const actionApplicability = (ndr: NDRRecord, action: ActionType): { ok: boolean; reason: string | null } => {
    const state = (ndr.order_shipping_status || '').toLowerCase();
    if (!state) return { ok: true, reason: null };
    if (ACTION_BLOCKED_STATES[action].includes(state)) {
      const moving = ['in_transit', 'out_for_delivery', 'booked', 'picked_up'].includes(state);
      return {
        ok: false,
        reason: moving
          ? `The shipment is currently "${state}" and progressing normally — there is no failed delivery to act on.`
          : `The shipment is currently "${state}", so this action no longer applies.`,
      };
    }
    return { ok: true, reason: null };
  };

  const submitAction = async () => {
    if (!actionModal) return;
    const { ndr, action } = actionModal;
    // Re-check applicability at submit time (state may have changed).
    const applicable = actionApplicability(ndr, action);
    if (!applicable.ok) { alert(applicable.reason || 'This action is not applicable.'); return; }
    if (action === 'mark_rto' && !rtoAcknowledged) { alert('Please confirm you understand this initiates an RTO.'); return; }
    setSubmitting(true);
    try {
      if (action === 'reattempt') {
        await api.post(`/api/ndr/${ndr.id}/reattempt`, { notes: actionNotes || undefined });
      } else if (action === 'reschedule') {
        if (!rescheduleDate) { alert('Please pick a reschedule date'); setSubmitting(false); return; }
        await api.post(`/api/ndr/${ndr.id}/reschedule`, { reschedule_date: rescheduleDate, notes: actionNotes || undefined });
      } else if (action === 'update_address') {
        if (!newAddress.trim()) { alert('Address is required'); setSubmitting(false); return; }
        await api.post(`/api/ndr/${ndr.id}/update-address`, { updated_address: newAddress.trim(), updated_phone: newPhone || undefined, notes: actionNotes || undefined });
      } else if (action === 'mark_rto') {
        await api.post(`/api/ndr/${ndr.id}/mark-rto`, { notes: actionNotes || undefined });
      } else if (action === 'close') {
        await api.post(`/api/ndr/${ndr.id}/close`, { resolution_notes: actionNotes || undefined });
      }
      setActionModal(null);
      await fetchData();
    } catch (err) {
      alert(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const SortHeader: React.FC<{ field: string; children: React.ReactNode }> = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      style={{
        padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600,
        textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px',
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      }}
    >
      {children}
      {sortField === field && <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  const tileStyle = (active: boolean, accent: string): React.CSSProperties => ({
    background: 'white', border: `1px solid ${active ? accent : '#e2e8f0'}`,
    borderRadius: '12px', padding: '14px 16px', cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: '150ms cubic-bezier(0.4,0,0.2,1)',
    borderLeft: `3px solid ${accent}`,
  });

  return (
    <div style={{ padding: '20px 24px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Failed Deliveries (NDR)
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
            Manage undelivered shipments — reattempt, reschedule, correct the address, or send to RTO.
          </p>
        </div>
        <button
          onClick={fetchData}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
            background: 'white', color: '#334155', border: '1px solid #e2e8f0',
            borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <CollapsibleFilters label="Summary & Filters">
        {summary && (
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>
            Tiles show {period === 'month' ? 'the current month' : period === 'all' ? 'all time' : `the ${period.replace('d', '')}-day window`} · use Reports for full history
          </div>
        )}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={tileStyle(openOnly, '#f59e0b')} onClick={() => { setOpenOnly(true); setFilterStatus(''); setPage(1); }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Open</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{summary.open}</div>
            </div>
            <div style={tileStyle(filterStatus === 'rto_initiated', '#ef4444')} onClick={() => { setOpenOnly(false); setFilterStatus('rto_initiated'); setPage(1); }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>RTO</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#ef4444' }}>{summary.rto_initiated}</div>
            </div>
            <div style={tileStyle(filterStatus === 'delivered', '#10b981')} onClick={() => { setOpenOnly(false); setFilterStatus('delivered'); setPage(1); }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recovered</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>{summary.delivered}</div>
            </div>
            <div style={tileStyle(!openOnly && !filterStatus, '#64748b')} onClick={() => { setOpenOnly(false); setFilterStatus(''); setPage(1); }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>{summary.total}</div>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px',
          padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px',
        }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Search</label>
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="NDR #, AWB, order, reason…"
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: 'white', width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Provider</label>
            <Typeahead value={filterProvider} onChange={(v) => { setFilterProvider(v); setPage(1); }} options={PROVIDER_OPTIONS} placeholder="All providers" />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Status</label>
            <Typeahead
              value={filterStatus}
              onChange={(v) => { setFilterStatus(v); setOpenOnly(false); setPage(1); }}
              options={STATUS_OPTIONS}
              placeholder="All statuses"
              labelMap={(o) => STATUS_BADGE[o]?.label || o}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Period</label>
            <select
              value={period}
              onChange={(e) => { setPeriod(e.target.value); setPage(1); }}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: 'white', width: '100%', boxSizing: 'border-box' }}
            >
              <option value="month">This month</option>
              <option value="90d">Last 90 days</option>
              <option value="180d">Last 180 days</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={openOnly} onChange={(e) => { setOpenOnly(e.target.checked); if (e.target.checked) setFilterStatus(''); setPage(1); }} />
              Open only
            </label>
          </div>
        </div>
      </CollapsibleFilters>

      {error && <ErrorDisplay error={error} onRetry={fetchData} />}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafb', borderBottom: '1px solid #e2e8f0' }}>
                  <SortHeader field="ndr_number">NDR #</SortHeader>
                  <SortHeader field="order_number">Order</SortHeader>
                  <SortHeader field="awb_number">AWB</SortHeader>
                  <SortHeader field="shipping_provider">Provider</SortHeader>
                  <SortHeader field="reason">Reason</SortHeader>
                  <SortHeader field="attempt_count">Attempts</SortHeader>
                  <SortHeader field="status">Status</SortHeader>
                  <SortHeader field="created_at">Raised</SortHeader>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedRecords.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                      No NDR records found. Failed deliveries reported by carriers appear here.
                    </td>
                  </tr>
                ) : sortedRecords.map((ndr) => {
                  const isOpen = OPEN_STATUSES.includes(ndr.status);
                  return (
                    <tr key={ndr.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                    >
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#1e293b' }}>{ndr.ndr_number}</td>
                      <td style={{ padding: '10px 12px' }}>{ndr.order_number || '—'}</td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#64748b' }}>{ndr.awb_number || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>{ndr.shipping_provider || '—'}</td>
                      <td style={{ padding: '10px 12px', maxWidth: '220px', color: '#334155' }} title={ndr.reason || ''}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ndr.reason || '—'}</div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{ fontWeight: 600, color: ndr.attempt_count > 2 ? '#ef4444' : '#334155' }}>{ndr.attempt_count}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}><StatusBadge status={ndr.status} /></td>
                      <td style={{ padding: '10px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {ndr.created_at ? new Date(ndr.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {isOpen && canAct ? (
                          <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <button onClick={() => openAction(ndr, 'reattempt')} style={btnSecondary}>Reattempt</button>
                            <button onClick={() => openAction(ndr, 'reschedule')} style={btnSecondary}>Reschedule</button>
                            <button onClick={() => openAction(ndr, 'update_address')} style={btnSecondary}>Address</button>
                            {canRTO && <button onClick={() => openAction(ndr, 'mark_rto')} style={btnDanger}>RTO</button>}
                            {canRTO && <button onClick={() => openAction(ndr, 'close')} style={btnSecondary}>Close</button>}
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '12px',
          }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Showing {startIdx} to {endIdx} of {total}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', background: 'white' }}
              >
                {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={pageBtn(page <= 1)}>Previous</button>
                <span style={{ fontSize: '13px', color: '#334155' }}>Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={pageBtn(page >= totalPages)}>Next</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !submitting && setActionModal(null)}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '480px', width: '90%', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>
              {ACTION_TITLES[actionModal.action]}
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 12px' }}>
              {actionModal.ndr.ndr_number} · Order {actionModal.ndr.order_number || '—'} · AWB {actionModal.ndr.awb_number || '—'}
              {actionModal.ndr.order_shipping_status && (
                <> · Current: <strong style={{ color: '#334155' }}>{actionModal.ndr.order_shipping_status}</strong></>
              )}
            </p>

            {/* Applicability warning — blocks confirm when the action doesn't
                fit the order's current shipping state (mirrors backend guard). */}
            {(() => {
              const { ok, reason } = actionApplicability(actionModal.ndr, actionModal.action);
              if (ok) return null;
              return (
                <div style={{ padding: '10px 12px', background: '#fff3cd', color: '#856404', borderRadius: '8px', fontSize: '13px', marginBottom: '12px', border: '1px solid #ffeeba' }}>
                  <strong>Not applicable.</strong> {reason} Refresh to see the latest status.
                </div>
              );
            })()}

            {/* What this action will do — confirmation context to catch mis-clicks. */}
            <div style={{ padding: '10px 12px', background: '#f1f5f9', color: '#334155', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
              {ACTION_EFFECT[actionModal.action]}
            </div>

            {actionModal.action === 'reschedule' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Reschedule date</label>
                <input type="date" value={rescheduleDate} min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setRescheduleDate(e.target.value)} style={inputStyle} />
              </div>
            )}

            {actionModal.action === 'update_address' && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Corrected address</label>
                  <textarea value={newAddress} onChange={(e) => setNewAddress(e.target.value)} rows={3}
                    placeholder="Full corrected delivery address" style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Contact phone (optional)</label>
                  <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="10-digit phone" style={inputStyle} />
                </div>
              </>
            )}

            {actionModal.action === 'mark_rto' && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', fontSize: '13px', marginBottom: '12px', border: '1px solid #fca5a5', cursor: 'pointer' }}>
                <input type="checkbox" checked={rtoAcknowledged} onChange={(e) => setRtoAcknowledged(e.target.checked)} style={{ marginTop: '2px' }} />
                <span>I understand this initiates a Return-to-Origin and cannot be undone from here.</span>
              </label>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{actionModal.action === 'close' ? 'Resolution notes' : 'Notes (optional)'}</label>
              <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} rows={2}
                placeholder="Add a note…" style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              {(() => {
                const applicable = actionApplicability(actionModal.ndr, actionModal.action).ok;
                const rtoBlocked = actionModal.action === 'mark_rto' && !rtoAcknowledged;
                const disabled = submitting || !applicable || rtoBlocked;
                return (
                  <>
                    <button onClick={() => setActionModal(null)} disabled={submitting} style={btnSecondaryLg}>Cancel</button>
                    <button onClick={submitAction} disabled={disabled}
                      style={{
                        ...(actionModal.action === 'mark_rto' ? btnDangerLg : btnPrimaryLg),
                        opacity: disabled ? 0.5 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}>
                      {submitting ? 'Working…' : ACTION_CONFIRM[actionModal.action]}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ACTION_TITLES: Record<ActionType, string> = {
  reattempt: 'Request Reattempt',
  reschedule: 'Reschedule Delivery',
  update_address: 'Update Delivery Address',
  mark_rto: 'Mark as RTO',
  close: 'Close NDR',
};
const ACTION_CONFIRM: Record<ActionType, string> = {
  reattempt: 'Request Reattempt',
  reschedule: 'Reschedule',
  update_address: 'Save Address',
  mark_rto: 'Confirm RTO',
  close: 'Close NDR',
};

const btnBase: React.CSSProperties = { borderRadius: '6px', fontSize: '12px', fontWeight: 600, padding: '4px 10px', cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { ...btnBase, background: 'white', color: '#334155', border: '1px solid #e2e8f0' };
const btnDanger: React.CSSProperties = { ...btnBase, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' };

const btnLg: React.CSSProperties = { borderRadius: '6px', fontSize: '13px', fontWeight: 600, padding: '6px 14px', cursor: 'pointer' };
const btnPrimaryLg: React.CSSProperties = { ...btnLg, background: '#0d9488', color: 'white', border: 'none' };
const btnSecondaryLg: React.CSSProperties = { ...btnLg, background: 'white', color: '#334155', border: '1px solid #e2e8f0' };
const btnDangerLg: React.CSSProperties = { ...btnLg, background: '#ef4444', color: 'white', border: 'none' };

const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' };
const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: 'white', width: '100%', boxSizing: 'border-box' };

const pageBtn = (disabled: boolean): React.CSSProperties => ({
  padding: '4px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
  background: 'white', color: disabled ? '#cbd5e1' : '#334155', cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

export default NDRManagement;
